import { Button, Form, message, Select, Table } from "antd";
import axios from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactToPrint from "react-to-print";
import LoadingBox from "../../Components/LoadingBox";
import { API, Headers } from "../../Config";
import PageHeader from "../../Routes/PageHeader";
import "./style.css";
import { Col, Row } from "react-bootstrap";

const StatementReportTable = () => {
  const [trialBalance, setTrialBalance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [indicator, setIndicator] = useState(false);
  const [awbbill, setAwbbill] = useState([]);

  const [totals, setTotals] = useState({});

  const componentRef = useRef();

  // Fetch trial balance data
  const fetchTrialBalanceData = useCallback(async (id) => {
    try {
      const response = await axios.get(
        `${API.BASR_URL}${API.GET_ALL_LEDGER_BY_CHRTACC}?id=${id ? id : ""}`,
        {
          headers: Headers(),
        }
      );

      if (response?.status === 200 && response.data?.accounts) {
        const transformedData = response?.data?.accounts.flatMap(
          (account) =>
            account.chartOfAccount?.accounts?.map((entry) => ({
              account_no: entry.account_no,
              account_name: entry.trans_type,
              note: entry.note,
              cr: entry.dr_or_cr === "Cr" ? entry.amount : 0,
              dr: entry.dr_or_cr === "Dr" ? entry.amount : 0,
            })) || []
        );

        setTrialBalance(transformedData);
        setTotals(response.data.totals || {});
        message.success("Data loaded successfully");
      } else {
        message.success(response?.message || "No data available");
      }
    } catch (error) {
      console.error("Error fetching trial balance data:", error);
      message.error("Oops. Something went wrong. Please try again later.");
      setTrialBalance([]);
      setTotals({});
    } finally {
      setLoading(false);
      setIndicator(false);
    }
  }, []);

  useEffect(() => {
    fetchTrialBalanceData();
    AwbBillsDropdown();
  }, [fetchTrialBalanceData]);

  const columns = useMemo(
    () => [
      {
        title: "Account No",
        dataIndex: "account_no",
        key: "account_no",
      },
      {
        title: "Account Name",
        dataIndex: "account_name",
        key: "account_name",
      },
      {
        title: "Airway Bill",
        dataIndex: "note",
        key: "note",
      },
      {
        title: "Debit",
        dataIndex: "dr",
        key: "dr",
        render: (item) => <span>{Number(item).toFixed(2)}</span>,
      },
      {
        title: "Credit",
        dataIndex: "cr",
        key: "cr",
        render: (item) => <span>{Number(item).toFixed(2)}</span>,
      },
    ],
    []
  );

  const summaryRow = useMemo(
    () => ({
      account_no: "",
      account_name: "Total",
      cr: totals.totalCrSum?.toFixed(2) || "0.00",
      dr: totals.totalDrSum?.toFixed(2) || "0.00",
      className: "summary-row",
    }),
    [totals.totalCrSum, totals.totalDrSum]
  );

  const balanceRow = useMemo(
    () => ({
      account_no: "",
      account_name: "Balance",
      cr:
        totals.totalDifference >= 0
          ? Math.abs(totals.totalDifference).toFixed(2)
          : "0.00",
      dr:
        totals.totalDifference < 0
          ? Math.abs(totals.totalDifference).toFixed(2)
          : "0.00",
      className: "balance-row",
    }),
    [totals.totalDifference]
  );

  const AwbBillsDropdown = () => {
    axios
      .get(API.BASR_URL + API.AIRWAY_BILLS_GEN + `/list`, {
        headers: Headers(),
      })
      .then(function (res) {
        if (res.status === 200) {
          setAwbbill(res.data);
        }
      })
      .catch(function (error) {
        message.error("Customers dropdown faild");
      });
  };

  const handleSelect = (data) => {
    fetchTrialBalanceData(data);
  };

  return (
    <div className="statement-report-page">
      <PageHeader
        PageName={"Statement Reports"}
        breadScrum={"Admin / Statement Reports"}
        loader={indicator}
      />
      <Form layout="vertical">
        <Row>
          <Col md={8} />
          <Col>
            <Form.Item name="awb_no" label="Search By Airway Bill">
              <Select
                size="large"
                placeholder="Select awb no"
                showSearch
                allowClear
                onChange={(val) => handleSelect(val)}
                filterOption={(input, option) => {
                  const accountNo = option.value.toLowerCase();
                  const accountName = option.children.toString().toLowerCase();
                  return (
                    accountNo.includes(input.toLowerCase()) ||
                    accountName.includes(input.toLowerCase())
                  );
                }}
              >
                {awbbill?.map((item, index) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.airline}
                    {" - "}
                    {item.awb_no}
                    {" - "}
                    {item.orijin_airport_name &&
                      item.orijin_airport_name} /{" "}
                    {item.destination_airport_name &&
                      item.destination_airport_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {loading ? (
        <LoadingBox />
      ) : trialBalance.length > 0 ? (
        <div>
          <div className="flex-container">
            <ReactToPrint
              trigger={() => (
                <Button type="primary" className="print-button">
                  Print
                </Button>
              )}
              content={() => componentRef.current}
            />
          </div>
          <div ref={componentRef}>
            <Table
              size="small"
              columns={columns}
              dataSource={[...trialBalance, summaryRow, balanceRow]}
              pagination={false}
              bordered
              rowClassName={(record) => record.className || ""}
            />
          </div>
        </div>
      ) : (
        <div className="no-data-message">No data available.</div>
      )}
    </div>
  );
};

export default StatementReportTable;
